import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import config from '../../../config'
import type { CreatePolizaFromCaratulaRequest, PolizaDTO, StorageFile, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke, { LONG_TIMEOUT_MS } from '../../../utils/APIInvoke'
import { AGENT_KEYS_PATH, POLICY_IMPORT_PATH, POLICY_IMPORT_PDF_PATH, policyEditPath } from '../common/policy-import-common'

export type PdfFileState = 'hold' | 'working' | 'done' | 'error'

export interface PdfFileItem {
    key: string
    file: File
    state: PdfFileState
    message: string | null
    poliza: PolizaDTO | null
}

export const PDF_ACCEPT = 'application/pdf,.pdf'

const UPLOAD_ERROR = 'No se pudo subir el archivo.'
const UNEXPECTED_ERROR = 'Se ha producido un error inesperado al importar la póliza'

export const TROUBLESHOOTING = [
    { id: 'clave', title: 'Clave de agente no encontrada', text: 'Es necesario dar de alta la clave de agente desde Configuración.', link: true },
    { id: 'tipo', title: 'No fue posible determinar el tipo de documento o aseguradora', text: 'Asegúrate de enviar la carátula original en PDF; si persiste, reporta la carátula a soporte para su análisis a contacto@kartera365.com.' },
    { id: 'campos', title: 'Algunos de los campos requeridos no fueron encontrados', text: 'Intenta procesar la carátula individualmente desde Pólizas › Nueva póliza.' },
    { id: 'grupo', title: 'Pólizas de grupo', text: 'Por ahora las pólizas de grupo no pueden importarse por este medio.' },
]

let fileSeq = 0

const isPdf = (file: File) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

const toItem = (file: File): PdfFileItem => ({ key: `pdf-${++fileSeq}`, file, state: 'hold', message: null, poliza: null })

export const usePolicyImportPdfModel = () => {
    const location = useLocation()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || POLICY_IMPORT_PATH
    const listRef = useRef<HTMLDivElement | null>(null)

    const [files, setFiles] = useState<PdfFileItem[]>([])
    const [working, setWorking] = useState(false)
    const [dragging, setDragging] = useState(false)
    const [rejected, setRejected] = useState(0)

    useEffect(() => {
        if (!working) return undefined
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            event.returnValue = ''
        }
        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [working])

    const patchFile = (key: string, partial: Partial<PdfFileItem>) =>
        setFiles((current) => current.map((item) => (item.key === key ? { ...item, ...partial } : item)))

    const addFiles = (list: FileList | File[] | null | undefined) => {
        if (!list || working) return
        const incoming = Array.from(list)
        const pdfs = incoming.filter(isPdf)
        setRejected(incoming.length - pdfs.length)
        if (pdfs.length) setFiles((current) => [...current, ...pdfs.map(toItem)])
    }

    const importOne = async (item: PdfFileItem) => {
        patchFile(item.key, { state: 'working', message: null })
        try {
            const body = new FormData()
            body.append('bucket', config.bucket.name)
            body.append('path', `polizas/${item.file.name}`)
            body.append('file', item.file)
            const uploaded: WrapperResponse<StorageFile> = await (await APIInvoke.uploadStorage(body)).json()
            if (uploaded.ok !== true || !uploaded.body) {
                patchFile(item.key, { state: 'error', message: uploaded.message || UPLOAD_ERROR })
                return
            }
            const request: CreatePolizaFromCaratulaRequest = { fileName: item.file.name, caratulaUrl: uploaded.body.url }
            const response: WrapperResponse<PolizaDTO> = await (await APIInvoke.invokePOST('/polizas/caratula', request, { timeoutMs: LONG_TIMEOUT_MS })).json()
            if (response.ok && response.body) patchFile(item.key, { state: 'done', message: 'Importación exitosa', poliza: response.body })
            else patchFile(item.key, { state: 'error', message: response.message || UNEXPECTED_ERROR })
        } catch (error) {
            console.error(error)
            patchFile(item.key, { state: 'error', message: UNEXPECTED_ERROR })
        }
    }

    const importAll = async () => {
        const pending = files.filter((item) => item.state === 'hold')
        if (!pending.length || working) return
        setWorking(true)
        listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        try {
            for (const item of pending) {
                // eslint-disable-next-line no-await-in-loop
                await importOne(item)
            }
        } finally {
            setWorking(false)
        }
    }

    const count = (state: PdfFileState) => files.filter((item) => item.state === state).length

    return {
        files,
        working,
        dragging,
        rejected,
        listRef,
        pending: count('hold'),
        done: count('done'),
        failed: count('error'),
        backTo: backUrl,
        agentKeysLocation: { pathname: AGENT_KEYS_PATH, state: { backUrl: POLICY_IMPORT_PDF_PATH } },
        policyPath: policyEditPath,
        addFiles,
        removeFile: (key: string) => { if (!working) setFiles((current) => current.filter((item) => item.key !== key)) },
        importAll,
        reset: () => {
            if (working) return
            setFiles([])
            setRejected(0)
        },
        drag: {
            onDragOver: (event: React.DragEvent) => {
                event.preventDefault()
                if (!working) setDragging(true)
            },
            onDragLeave: () => setDragging(false),
            onDrop: (event: React.DragEvent) => {
                event.preventDefault()
                setDragging(false)
                addFiles(event.dataTransfer.files)
            },
        },
    }
}
